// frontend/src/pages/DeviceManagement.js

import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  Spinner,
  Form,
  Row,
  Col,
  Table,
  InputGroup,
  FormControl,
  Pagination,
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const DeviceManagement = () => {
  const { user } = useAuth(); // 管理员权限检测

  // 设备 & 分区
  const [devices, setDevices] = useState([]);
  const [partitions, setPartitions] = useState([]);
  const [loading, setLoading] = useState(false);

  // 分配设备状态
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignDevice, setAssignDevice] = useState(null);
  const [assignUsername, setAssignUsername] = useState('');

  // “数据”弹窗统计
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsDevice, setStatsDevice] = useState(null);
  const [statsBetTotal, setStatsBetTotal] = useState('0.0');
  const [statsPayoutTotal, setStatsPayoutTotal] = useState('0.0');
  const [statsProfitTotal, setStatsProfitTotal] = useState('0.0');

  // “记录”弹窗——回合盈亏
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsDevice, setLogsDevice] = useState(null);
  const [deviceRounds, setDeviceRounds] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const logsLimit = 10;

  // “账目”弹窗——分页 & 过滤
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerDevice, setLedgerDevice] = useState(null);
  const [roundsPage, setRoundsPage] = useState(1);
  const [roundsTotalPages, setRoundsTotalPages] = useState(1);
  const roundsLimit = 10;
  const [filterDateTime, setFilterDateTime] = useState('');

  // “下分记录”弹窗 (FS)
  const [showDebitsModal, setShowDebitsModal] = useState(false);
  const [debitsDevice, setDebitsDevice] = useState(null);
  const [debits, setDebits] = useState([]);
  const [debitsPage, setDebitsPage] = useState(1);
  const [debitsTotalPages, setDebitsTotalPages] = useState(1);
  const debitsLimit = 10;
  // 删除设备
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  // 编辑履历
  const [showHistogramModal, setShowHistogramModal] = useState(false);
  const [histogramDevice, setHistogramDevice] = useState(null);
  const [histogramData, setHistogramData] = useState([]);
  const [editingHistogram, setEditingHistogram] = useState(false);

  // 分区管理
  const [showAddPartitionModal, setShowAddPartitionModal] = useState(false);
  const [newPartitionName, setNewPartitionName] = useState('');
  const [newPartitionDesc, setNewPartitionDesc] = useState('');
  const [showEditPartitionModal, setShowEditPartitionModal] = useState(false);
  const [editPartitionId, setEditPartitionId] = useState(null);
  const [editPartitionName, setEditPartitionName] = useState('');
  const [editPartitionDesc, setEditPartitionDesc] = useState('');
  const [showDeletePartitionModal, setShowDeletePartitionModal] = useState(false);
  const [partitionToDelete, setPartitionToDelete] = useState(null);
  // 设备搜索
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [partitionFilter, setPartitionFilter] = useState('all');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  // 新增：设备异常状态缓存
  const [deviceStatusMap, setDeviceStatusMap] = useState({});

  const navigate = useNavigate();  useEffect(() => {
    fetchDevices();
    fetchPartitions();
  }, []);

  // 当筛选条件改变时重新获取数据（但不包括搜索词变化）
  useEffect(() => {
    if (!loading) {
      fetchDevices();
    }
  }, [statusFilter, partitionFilter]);
  /** 获取所有设备 */
  const fetchDevices = async (resetSearch = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 搜索参数
      if (searchTerm.trim() && !resetSearch) {
        params.append('search', searchTerm.trim());
      }
      
      // 状态筛选
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      // 分区筛选
      if (partitionFilter !== 'all') {
        params.append('location', partitionFilter);
      }
      
      const url = params.toString() ? `/api/devices?${params.toString()}` : '/api/devices';
      const res = await api.get(url);
      setDevices(res.data);
    } catch (err) {
      console.error('获取设备列表失败:', err);
      toast.error(err.response?.data?.message || '获取设备列表失败');
    } finally {
      setLoading(false);
    }
  };

  /** 获取分区列表 */
  const fetchPartitions = async () => {
    try {
      const res = await api.get('/api/partitions');
      setPartitions(res.data);
    } catch (err) {
      console.error('获取分区列表失败:', err);
      toast.error(err.response?.data?.message || '获取分区列表失败');
    }
  };

  /** 删除设备 */
  const handleDeleteDevice = device => {
    setDeviceToDelete(device);
    setShowDeleteModal(true);
  };
  const confirmDeleteDevice = async () => {
    try {
      await api.delete(`/api/devices/${deviceToDelete._id}`);
      toast.success('设备删除成功');
      setDevices(prev => prev.filter(d => d._id !== deviceToDelete._id));
    } catch (err) {
      console.error('删除设备失败:', err);
      toast.error(err.response?.data?.message || '删除失败');
    } finally {
      setShowDeleteModal(false);
      setDeviceToDelete(null);
    }
  };

  /** 编辑设备 */
  const handleEdit = id => navigate(`/admin/devices/edit/${id}`);

  /** 释放占用 */
  const handleRelease = async device => {
    if (device.status !== 'occupied') {
      toast.warn('该设备未占用');
      return;
    }
    try {
      // 管理员使用强制释放，其他角色使用标准释放
      const url = user.role === 'admin'
        ? `/api/devices/${device._id}/force-release`
        : `/api/devices/${device._id}/release`;
      await api.post(url);
      toast.success('已释放占用');
      fetchDevices();
    } catch (err) {
      console.error('释放失败:', err);
      toast.error(err.response?.data?.message || '释放失败');
    }
  };

  /** 设置维修状态 */
  const handleSetMaintenance = async (device) => {
    try {
      await api.post(`/api/devices/${device._id}/maintain`);
      toast.success('设备已设置为维修状态');
      fetchDevices();
    } catch (err) {
      toast.error('操作失败');
    }
  };

  /** 解除维修状态 */
  const handleClearMaintenance = async (device) => {
    try {
      await api.post(`/api/devices/${device._id}/unmaintain`);
      toast.success('已解除维修状态');
      fetchDevices();
    } catch (err) {
      toast.error('操作失败');
    }
  };

  /** 分配设备 */
  const handleAssignDevice = async () => {
    if (!assignUsername.trim()) {
      toast.warn('用户名不能为空');
      return;
    }
    try {
      await api.post(`/api/devices/${assignDevice._id}/assign`, { username: assignUsername.trim() });
      toast.success('设备分配成功');
      fetchDevices();
      setShowAssignModal(false);
      setAssignDevice(null);
      setAssignUsername('');
    } catch (err) {
      console.error('分配设备失败:', err);
      toast.error(err.response?.data?.message || '分配设备失败');
    }
  };

  // 分配设备弹窗确认
  const handleConfirmAssign = async () => {
    if (!assignDevice || !assignUsername.trim()) return;
    try {
      await api.post(`/api/devices/${assignDevice._id}/assign`, { targetUsername: assignUsername });
      toast.success('分配成功');
      setShowAssignModal(false);
      setAssignUsername('');
      fetchDevices();
    } catch (err) {
      toast.error(err.response?.data?.message || '分配失败');
    }
  };

  /** “数据”按钮 — 计算总投注、总返还、总盈亏 */
  const handleShowStats = async device => {
    setStatsDevice(device);
    setShowStatsModal(true);
    try {
      const res = await api.get(`/api/ledger/device/${device._id}/rounds?limit=100000`);
      const rounds = res.data.rounds || [];
      const betSum = rounds.reduce((sum, r) => sum + Number(r.betTotal || 0), 0);
      const payoutSum = rounds.reduce((sum, r) => sum + Number(r.payoutTotal || 0), 0);
      setStatsBetTotal(betSum.toFixed(1));
      setStatsPayoutTotal(payoutSum.toFixed(1));
      // 修正利润计算：系统角度应该是 投注 - 赔付
      setStatsProfitTotal((betSum - payoutSum).toFixed(1));
    } catch (err) {
      console.error('计算统计失败:', err);
      toast.error('计算统计失败');
    }
  };

  /** 拉取回合账单 */
  const fetchRounds = async (deviceId, page, filter) => {
    const query = [];
    if (filter) query.push(`filter=${encodeURIComponent(filter)}`);
    query.push(`page=${page}`, `limit=${roundsLimit}`);
    const res = await api.get(`/api/ledger/device/${deviceId}/rounds?${query.join('&')}`);
    return res.data;
  };

  /** “记录”按钮 — 回合盈亏列表 */
  const handleShowLogs = async device => {
    setLogsDevice(device);
    setShowLogsModal(true);
    setDeviceRounds([]);
    setLogsPage(1);
    try {
      const data = await fetchRounds(device._id, 1, '');
      setDeviceRounds(data.rounds || []);
      setLogsTotalPages(Math.ceil((data.rounds || []).length / logsLimit));
    } catch (err) {
      console.error('获取回合记录失败:', err);
      toast.error('获取记录失败');
    }
  };
  const goPrevLogs = async () => {
    if (logsPage <= 1) return;
    const newPage = logsPage - 1;
    try {
      const data = await fetchRounds(logsDevice._id, newPage, '');
      setDeviceRounds(data.rounds || []);
      setLogsPage(newPage);
    } catch (err) {
      console.error(err);
    }
  };
  const goNextLogs = async () => {
    if (logsPage >= logsTotalPages) return;
    const newPage = logsPage + 1;
    try {
      const data = await fetchRounds(logsDevice._id, newPage, '');
      setDeviceRounds(data.rounds || []);
      setLogsPage(newPage);
    } catch (err) {
      console.error(err);
    }
  };

  /** “账目”按钮 — 分页 & 过滤 */
  const handleShowLedger = async device => {
    setLedgerDevice(device);
    setShowLedgerModal(true);
    setFilterDateTime('');
    setDeviceRounds([]);
    setRoundsPage(1);
    try {
      const data = await fetchRounds(device._id, 1, '');
      setDeviceRounds(data.rounds || []);
      setRoundsTotalPages(Math.ceil((data.rounds || []).length / roundsLimit));
    } catch (err) {
      console.error('获取账目失败:', err);
      toast.error('获取账目失败');
    }
  };
  const applyFilter = async () => {
    try {
      const data = await fetchRounds(
        ledgerDevice._id,
        1,
        filterDateTime.trim()
      );
      setDeviceRounds(data.rounds || []);
      setRoundsPage(1);
      setRoundsTotalPages(Math.ceil((data.rounds || []).length / roundsLimit));
    } catch (err) {
      console.error('过滤失败:', err);
      toast.error('过滤失败');
    }
  };
  const goPrevRounds = async () => {
    if (roundsPage <= 1) return;
    const newPage = roundsPage - 1;
    try {
      const data = await fetchRounds(ledgerDevice._id, newPage, filterDateTime);
      setDeviceRounds(data.rounds || []);
      setRoundsPage(newPage);
    } catch (err) {
      console.error(err);
    }
  };
  const goNextRounds = async () => {
    if (roundsPage >= roundsTotalPages) return;
    const newPage = roundsPage + 1;
    try {
      const data = await fetchRounds(ledgerDevice._id, newPage, filterDateTime);
      setDeviceRounds(data.rounds || []);
      setRoundsPage(newPage);
    } catch (err) {
      console.error(err);
    }
  };
  const handleDeleteAllRounds = async () => {
    if (!ledgerDevice) return;
    try {
      await api.delete(`/api/ledger/device/${ledgerDevice._id}/rounds`);
      setDeviceRounds([]);
      toast.success('已删除所有账目');
    } catch (err) {
      console.error('删除账目失败:', err);
      toast.error('删除账目失败');
    }
  };

  /** 分区管理 */
  const openAddPartitionModal = () => {
    setShowAddPartitionModal(true);
    setNewPartitionName('');
    setNewPartitionDesc('');
  };
  const closeAddPartitionModal = () => setShowAddPartitionModal(false);
  const handleSavePartition = async () => {
    if (!newPartitionName.trim()) {
      toast.warn('分区名称不能为空');
      return;
    }
    try {
      await api.post('/api/partitions', {
        name: newPartitionName.trim(),
        description: newPartitionDesc.trim(),
      });
      toast.success('分区添加成功');
      fetchPartitions();
      closeAddPartitionModal();
    } catch (err) {
      toast.error(err.response?.data?.message || '添加分区失败');
    }
  };

  const openEditPartitionModal = p => {
    setEditPartitionId(p._id);
    setEditPartitionName(p.name);
    setEditPartitionDesc(p.description || '');
    setShowEditPartitionModal(true);
  };
  const closeEditPartitionModal = () => setShowEditPartitionModal(false);
  const handleUpdatePartition = async () => {
    if (!editPartitionName.trim()) {
      toast.warn('分区名称不能为空');
      return;
    }
    try {
      await api.put(`/api/partitions/${editPartitionId}`,
        { name: editPartitionName.trim(), description: editPartitionDesc.trim() });
      toast.success('分区更新成功');
      fetchPartitions();
      closeEditPartitionModal();
    } catch (err) {
      toast.error(err.response?.data?.message || '更新分区失败');
    }
  };
  const handleDeletePartition = p => {
    setPartitionToDelete(p);
    setShowDeletePartitionModal(true);
  };
  const cancelDeletePartition = () => setShowDeletePartitionModal(false);
  const confirmDeletePartition = async () => {
    if (!partitionToDelete) return;
    try {
      await api.delete(`/api/partitions/${partitionToDelete._id}`);
      toast.success('分区删除成功');
      fetchPartitions();
    } catch (err) {
      toast.error(err.response?.data?.message || '删除分区失败');
    } finally {
      setShowDeletePartitionModal(false);
      setPartitionToDelete(null);
    }
  };  /** 设备搜索（模糊） */
  const handleSearch = async e => {
    e.preventDefault();
    console.log('设备搜索提交 - 搜索词:', searchTerm, '状态:', statusFilter, '分区:', partitionFilter);
    setLoading(true);
    try {
      await fetchDevices(false); // 明确传递 false，表示不重置搜索
      console.log('搜索完成');
    } catch (err) {
      console.error('设备搜索异常', err);
      toast.error('搜索失败，请重试');
    }
  };
  
  const handleReset = async () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPartitionFilter('all');
    await fetchDevices(true);
  };
  /** 获取 FS 下分记录 */
  const fetchDebits = async (deviceId, page = 1) => {
    const res = await api.get(`/api/devices/${deviceId}/debits?page=${page}&limit=${debitsLimit}`);
    setDebits(res.data.debits || []);
    setDebitsPage(res.data.currentPage || 1);
    setDebitsTotalPages(res.data.totalPages || 1);
  };
  /** 显示编辑履历弹窗 */
  const handleShowHistogram = async (device) => {
    setHistogramDevice(device);
    setShowHistogramModal(true);
    // 初始化历史数据，如果没有数据则创建默认结构
    const histograms = device.histogramBars || [];
    // 确保最多20条记录，并且有完整的结构
    const formattedHistograms = histograms.slice(0, 20).map(item => ({
      consecutivePrize: item.consecutivePrize || 0,
      turnCount: item.turnCount || 0,
      timestamp: item.timestamp ? new Date(item.timestamp) : new Date()
    }));
    while (formattedHistograms.length < 20) {
      formattedHistograms.push({
        consecutivePrize: 0,
        turnCount: 0,
        timestamp: new Date()
      });
    }
    setHistogramData(formattedHistograms);
  };

  /** 更新履历数据字段 */
  const updateHistogramField = (index, field, value) => {
    const newData = [...histogramData];
    newData[index] = {
      ...newData[index],
      [field]: field === 'timestamp' ? value : Number(value) || 0
    };
    setHistogramData(newData);
  };
  /** 删除某条履历 */
  const handleDeleteHistogramRow = (index) => {
    const newData = histogramData.filter((_, i) => i !== index);
    // 保证最多20条
    while (newData.length < 20) {
      newData.push({ consecutivePrize: 0, turnCount: 0, timestamp: new Date() });
    }
    setHistogramData(newData);
  };

  /** 新增一条履历 */
  const handleAddHistogramRow = () => {
    if (histogramData.length >= 20) return;
    setHistogramData([
      ...histogramData,
      { consecutivePrize: 0, turnCount: 0, timestamp: new Date() }
    ]);
  };

  /** 保存履历数据 */
  const handleSaveHistogram = async () => {
    if (!histogramDevice) return;
    
    setEditingHistogram(true);
    try {      // 过滤掉空记录（consecutivePrize和turnCount都为0的记录）
      const validHistograms = histogramData.filter(item =>
        item.consecutivePrize > 0 || item.turnCount > 0
      ).slice(0, 20);
      // 校验
      for (const item of validHistograms) {
        if (item.consecutivePrize < 0 || item.turnCount < 0) {
          toast.error('奖项数和转数不能为负数');
          setEditingHistogram(false);
          return;
        }
      }
      await api.put(`/api/devices/${histogramDevice._id}/histogram`, {
        histogramBars: validHistograms
      });
      
      toast.success('履历数据更新成功');
      setShowHistogramModal(false);
      fetchDevices(); // 刷新设备列表
    } catch (err) {
      console.error('更新履历失败:', err);
      toast.error(err.response?.data?.message || '更新履历失败');
    } finally {
      setEditingHistogram(false);
    }
  };

  /** 显示 FS 下分记录 */
  const handleShowDebits = device => {
    setDebitsDevice(device);
    setShowDebitsModal(true);
    fetchDebits(device._id, 1);
  };
  const goPrevDebits = () => {
    if (debitsPage > 1) fetchDebits(debitsDevice._id, debitsPage - 1);
  };
  const goNextDebits = () => {
    if (debitsPage < debitsTotalPages) fetchDebits(debitsDevice._id, debitsPage + 1);
  };
  /** 新增：拉取所有设备的状态 */
  const fetchAllDeviceStatus = async (deviceList) => {
    const statusMap = {};
    let dirtyFixed = false;
    await Promise.all(deviceList.map(async (dev) => {
      let statusInfo = {};
      try {
        const res = await api.get(`/api/devices/${dev._id}/status`);
        statusInfo = res.data;
        statusMap[dev._id] = statusInfo;
      } catch (err) {
        // 500等异常也视为脏占用，尝试自动修正
        statusMap[dev._id] = { error: true };
        try {
          await api.post(`/api/devices/${dev._id}/force-release`);
          dirtyFixed = true;
        } catch {}
      }
      // 正常返回但为脏占用也修正
      const isDirtyOccupied = dev.status === 'occupied' && (!statusInfo.isGaming || !dev.currentHolder);
      if (!statusMap[dev._id].error && isDirtyOccupied) {
        try {
          await api.post(`/api/devices/${dev._id}/force-release`);
          dirtyFixed = true;
        } catch {}
      }
    }));
    setDeviceStatusMap(statusMap);
    if (dirtyFixed) fetchDevices(); // 修正后刷新
  };

  // 自动修正脏占用设备
  const autoFixDirtyDevices = async (deviceList, statusMap) => {
    await Promise.all(deviceList.map(async (dev) => {
      const statusInfo = statusMap[dev._id] || {};
      const isDirtyOccupied = dev.status === 'occupied' && (!statusInfo.isGaming || !dev.currentHolder);
      if (isDirtyOccupied) {
        try {
          await api.post(`/api/devices/${dev._id}/force-release`);
          // 可选：toast.info(`已自动修正设备 ${dev.name}`);
        } catch {}
      }
    }));
  };

  // 拉取设备和状态，定时刷新
  useEffect(() => {
    let timer;
    const load = async () => {
      await fetchDevices();
    };
    load();
    timer = setInterval(() => {
      if (devices.length > 0) fetchAllDeviceStatus(devices);
    }, 30000); // 30秒刷新
    return () => clearInterval(timer);
  }, []);

  // 每次设备列表变化时，拉取状态并自动修正
  useEffect(() => {
    if (devices.length > 0) fetchAllDeviceStatus(devices);
  }, [devices]);

  return (
    <div style={{ 
      padding: '20px', 
      background: 'linear-gradient(120deg,#f8fcff 60%,#e0f7fa 100%)', 
      minHeight: '100vh' 
    }}>
      <h2 style={{
        color:'#0cf',
        fontWeight:900,
        letterSpacing:2,
        textAlign:'center',
        textShadow:'0 2px 8px #0cf8',
        marginBottom: 30
      }}>
        设备管理系统
      </h2>
      
      {/* 搜索和筛选区域 */}
      <div className="mb-4">
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          border: '2px solid #0cf',
          borderRadius: 15,
          padding: 20,
          boxShadow: '0 4px 20px rgba(0,204,255,0.3)'
        }}>
          <div style={{
            background: 'linear-gradient(90deg,#0cf,#0ff)',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <div className="d-flex justify-content-between align-items-center">
              <span>🔍 搜索与筛选</span>
              <Button 
                variant="outline-light" 
                size="sm"
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              >
                {showAdvancedSearch ? '收起高级筛选' : '展开高级筛选'}
              </Button>
            </div>
          </div>
          
          {/* 基础搜索 */}
          <Form onSubmit={handleSearch}>
            <Row className="align-items-end">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>🔍 搜索设备</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="输入设备名称、ID或描述进行搜索..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={{
                      borderRadius: 10,
                      border: '2px solid #0cf',
                      boxShadow: '0 2px 10px rgba(0,204,255,0.2)'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Button 
                  variant="primary" 
                  type="submit" 
                  style={{
                    background: 'linear-gradient(90deg,#0cf,#0ff)',
                    border: 'none',
                    borderRadius: 10,
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                >
                  🔍 搜索
                </Button>
              </Col>
              <Col md={3}>
                <Button 
                  variant="outline-secondary"
                  onClick={handleReset}
                  style={{borderRadius: 10, width: '100%'}}
                >
                  🔄 重置
                </Button>
              </Col>
            </Row>
            
            {/* 高级筛选 */}
            {showAdvancedSearch && (
              <div className="mt-4 pt-3" style={{borderTop: '1px solid #e0e0e0'}}>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>📊 状态筛选</Form.Label>
                      <Form.Select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        style={{borderRadius: 8}}
                      >
                        <option value="all">全部状态</option>
                        <option value="idle">空闲</option>
                        <option value="occupied">占用</option>
                        <option value="维修">维修</option>
                        <option value="可用">可用</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>🏢 分区筛选</Form.Label>
                      <Form.Select
                        value={partitionFilter}
                        onChange={e => setPartitionFilter(e.target.value)}
                        style={{borderRadius: 8}}
                      >
                        <option value="all">全部分区</option>
                        {partitions.map(partition => (
                          <option key={partition._id} value={partition._id}>
                            {partition.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>🎮 设备类型</Form.Label>
                      <Form.Select style={{borderRadius: 8}}>
                        <option value="all">全部类型</option>
                        <option value="pinball">钢珠机</option>
                        <option value="sanqi">三七机</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}
          </Form>
        </div>
      </div>

      {/* 工具栏 */}
      <Row className="mb-3">
        <Col md={6}>
          <Link to="/admin/devices/add">
            <Button variant="success" style={{borderRadius: 10, marginRight: 10}}>
              ➕ 添加设备
            </Button>
          </Link>
          <Button variant="info" onClick={openAddPartitionModal} style={{borderRadius: 10}}>
            🏢 添加分区
          </Button>
        </Col>
        <Col md={6} className="text-end">
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: 10,
            display: 'inline-block'
          }}>
            📊 总设备数：{devices.length}
          </div>
        </Col>
      </Row>      {loading ? (
        <div style={{textAlign:'center',marginTop:80}}>
          <Spinner animation="border" variant="info" style={{width:60,height:60}}/>
          <div style={{color:'#0cf',marginTop:16,fontSize:18}}>加载中...</div>
        </div>
      ) : devices.length === 0 ? (
        <div className="text-center" style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: 15, 
          padding: 40,
          border: '2px solid #0cf'
        }}>
          <h4 style={{color: '#888'}}>😔 暂无设备数据</h4>
          <p style={{color: '#aaa'}}>没有找到符合条件的设备</p>
        </div>
      ) : (
        <Row>
          {devices.map(device => {
            const isOccupied = device.status === 'occupied';
            const isMaintenance = device.status === '维修';
            const statusColor = isOccupied ? '#ff6b6b' : isMaintenance ? '#feca57' : '#26de81';
            // 新增：异常占用判断
            const statusInfo = deviceStatusMap[device._id] || {};
            const isDirtyOccupied = isOccupied && (!statusInfo.isGaming || !device.currentHolder);
            return (
              <Col md={6} lg={4} key={device._id} className="mb-4">
                <div style={{
                  borderRadius: 15,
                  border: isDirtyOccupied ? '2.5px solid #ff1744' : '2px solid #0cf',
                  boxShadow: isDirtyOccupied ? '0 0 16px 2px #ff1744' : '0 4px 20px rgba(0,204,255,0.2)',
                  transition: 'all 0.3s ease',
                  background: isDirtyOccupied ? 'linear-gradient(135deg,#fff0f0 0%,#ffeaea 100%)' : 'linear-gradient(135deg, #fff 0%, #f0f9ff 100%)',
                  overflow: 'hidden',
                  position: 'relative'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,204,255,0.4)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,204,255,0.2)';
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #0cf 0%, #0ff 100%)',
                    color: 'white',
                    padding: '15px',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}>
                    🎮 {device.name} 
                    <small style={{opacity: 0.8}}> ({device._id.slice(-4)})</small>
                  </div>
                  
                  <div style={{ padding: '20px' }}>
                    <div className="device-info">
                      <p><strong>🏢 分区：</strong>{device.location?.name || '未分配'}</p>
                      <p>
                        <strong>📊 状态：</strong>
                        <span style={{
                          color: statusColor,
                          fontWeight: 'bold',
                          marginLeft: '8px'
                        }}>
                          {device.status}
                        </span>
                        {isOccupied && (
                          <Button
                            size="sm"
                            variant="outline-info"
                            className="ms-2"
                            onClick={() => handleRelease(device)}
                            style={{borderRadius: 8}}
                          >
                            🔓 释放
                          </Button>
                        )}
                      </p>
                      <p><strong>📝 简介：</strong>{device.description || '无'}</p>
                      <p><strong>👤 占用者：</strong>{device.currentHolder?.username || '—'}</p>
                      <p><strong>🎯 类型：</strong>{device.type === 'sanqi' ? '三七机' : '钢珠机'}</p>
                    </div>
                    
                    {/* 操作按钮 */}
                    <Row className="mt-3">
                      <Col xs={6}>
                        <Button 
                          variant="warning" 
                          size="sm" 
                          onClick={() => handleEdit(device._id)}
                          style={{width: '100%', borderRadius: 8}}
                        >
                          ✏️ 编辑
                        </Button>
                      </Col>
                      <Col xs={6}>
                        <Button 
                          variant="danger" 
                          size="sm" 
                          onClick={() => handleDeleteDevice(device)}
                          style={{width: '100%', borderRadius: 8}}
                        >
                          🗑️ 删除
                        </Button>
                      </Col>
                    </Row>
                    <Row className="mt-1">
                      <Col xs={4}>
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          onClick={() => handleShowStats(device)}
                          style={{width: '100%', borderRadius: 8}}
                        >
                          📊 数据
                        </Button>
                      </Col>
                      <Col xs={4}>
                        <Button 
                          variant="dark" 
                          size="sm" 
                          onClick={() => handleShowLogs(device)}
                          style={{width: '100%', borderRadius: 8}}
                        >
                          📋 记录
                        </Button>
                      </Col>
                      <Col xs={4}>
                        <Button 
                          variant="info" 
                          size="sm" 
                          onClick={() => handleShowLedger(device)}
                          style={{width: '100%', borderRadius: 8}}
                        >
                          💼 账目
                        </Button>
                      </Col>
                    </Row>
                    <Row className="mt-1">
                      <Col xs={12}>
                        <Button 
                          variant="primary" 
                          size="sm" 
                          onClick={() => handleShowDebits(device)}
                          style={{width: '100%', borderRadius: 8}}
                        >
                          💰 下分记录
                        </Button>
                      </Col>
                    </Row>
                    
                    {/* 管理员专用按钮 */}
                    {user?.role === 'admin' && (
                      <Row className="mt-2">
                        <Col xs={6}>
                          <Button 
                            variant="outline-warning" 
                            size="sm" 
                            onClick={() => { setAssignDevice(device); setShowAssignModal(true); }}
                            style={{width: '100%', borderRadius: 8}}
                          >
                            👥 分配
                          </Button>
                        </Col>
                        <Col xs={6}>
                          {!isMaintenance ? (
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              onClick={() => handleSetMaintenance(device)}
                              style={{width: '100%', borderRadius: 8}}
                            >
                              🔧 维修
                            </Button>
                          ) : (
                            <Button 
                              variant="outline-success" 
                              size="sm" 
                              onClick={() => handleClearMaintenance(device)}
                              style={{width: '100%', borderRadius: 8}}
                            >
                              ✅ 解除维修
                            </Button>
                          )}
                        </Col>
                        <Col xs={12} className="mt-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowHistogram(device)}
                            style={{ width: '100%', borderRadius: 8 }}
                          >
                            📝 编辑履历
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      )}

      {/* 删除设备 Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton><Modal.Title>确认删除</Modal.Title></Modal.Header>
        <Modal.Body>确认删除设备 “{deviceToDelete?.name}”？此操作不可撤销。</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>取消</Button>
          <Button variant="danger" onClick={confirmDeleteDevice}>删除</Button>
        </Modal.Footer>
      </Modal>

      {/* 分配设备 Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
        <Modal.Header closeButton><Modal.Title>分配设备</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Label>玩家用户名</Form.Label>
            <Form.Control value={assignUsername} onChange={e => setAssignUsername(e.target.value)} placeholder="请输入用户名" />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>取消</Button>
          <Button variant="primary" onClick={handleConfirmAssign}>确认</Button>
        </Modal.Footer>
      </Modal>

      {/* “数据” Modal */}
      <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)}>
        <Modal.Header closeButton><Modal.Title>设备统计</Modal.Title></Modal.Header>
        <Modal.Body>
          <p><strong>设备：</strong>{statsDevice?.name}</p>
          <div style={{ padding: '10px', border: '1px solid #ccc' }}>
            <p><strong>总投注：</strong>{statsBetTotal}</p>
            <p><strong>总返还：</strong>{statsPayoutTotal}</p>
            <p><strong>总盈亏：</strong>{statsProfitTotal}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatsModal(false)}>关闭</Button>
        </Modal.Footer>
      </Modal>

      {/* “记录” Modal（回合盈亏） */}
      <Modal 
        show={showLogsModal} 
        onHide={() => setShowLogsModal(false)} 
        size="xl"
        dialogClassName="premium-device-logs-modal"
        backdrop="static"
        centered
      >
        <Modal.Header className="premium-modal-header" closeButton>
          <Modal.Title className="premium-modal-title">
            <div className="title-content">
              <div className="title-icon">📊</div>
              <div className="title-text">
                <h4>回合盈亏记录</h4>
                <span className="device-name">{logsDevice?.name}</span>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="premium-modal-body">
          {deviceRounds.length > 0 ? (
            <>
              {/* 统计概览卡片 */}
              <div className="device-stats-overview">
                <div className="device-stat-card total-rounds">
                  <div className="stat-icon">🎮</div>
                  <div className="stat-content">
                    <div className="stat-number">{deviceRounds.length}</div>
                    <div className="stat-label">总回合</div>
                  </div>
                </div>
                <div className="device-stat-card total-bet">
                  <div className="stat-icon">💸</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {deviceRounds.reduce((sum, r) => sum + Number(r.betTotal || 0), 0).toFixed(1)}
                    </div>
                    <div className="stat-label">总投注</div>
                  </div>
                </div>
                <div className="device-stat-card total-payout">
                  <div className="stat-icon">💰</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {deviceRounds.reduce((sum, r) => sum + Number(r.payoutTotal || 0), 0).toFixed(1)}
                    </div>
                    <div className="stat-label">总返还</div>
                  </div>
                </div>
                <div className="device-stat-card net-profit">
                  <div className="stat-icon">📈</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {deviceRounds.reduce((sum, r) => sum + (Number(r.betTotal || 0) - Number(r.payoutTotal || 0)), 0).toFixed(1)}
                    </div>
                    <div className="stat-label">净盈亏</div>
                  </div>
                </div>
              </div>

              {/* 移动端卡片视图 */}
              <div className="mobile-card-view d-md-none">
                {deviceRounds.map(r => (
                  <div key={r._id} className="record-card">
                    <div className="card-header">
                      <span className="record-id">#{r._id.slice(-5)}</span>
                      <span className="record-player">{r.playerName || '未知'}</span>
                    </div>
                    <div className="card-body">
                      <div className="record-row">
                        <span className="label">投注:</span>
                        <span className="value">{Number(r.betTotal).toFixed(1)}</span>
                      </div>
                      <div className="record-row">
                        <span className="label">返还:</span>
                        <span className="value">{Number(r.payoutTotal).toFixed(1)}</span>
                      </div>
                      <div className="record-row">
                        <span className="label">盈亏:</span>
                        <span className={`value ${(Number(r.betTotal) - Number(r.payoutTotal)) >= 0 ? 'profit' : 'loss'}`}>
                          {(Number(r.betTotal) - Number(r.payoutTotal)).toFixed(1)}
                        </span>
                      </div>
                      <div className="record-time">
                        {new Date(r.endTime || r.startTime).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 桌面端表格视图 */}
              <div className="desktop-table-view d-none d-md-block">
                <div className="premium-table-container">
                  <Table className="premium-table">
                    <thead>
                      <tr>
                        <th>回合ID</th>
                        <th>玩家</th>
                        <th>投注总额</th>
                        <th>返还总额</th>
                        <th>盈亏</th>
                        <th>时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceRounds.map(r => (
                        <tr key={r._id}>
                          <td className="record-id">#{r._id.slice(-5)}</td>
                          <td className="player-name">{r.playerName || '未知'}</td>
                          <td className="amount">{Number(r.betTotal).toFixed(1)}</td>
                          <td className="amount">{Number(r.payoutTotal).toFixed(1)}</td>
                          <td className={`amount ${(Number(r.betTotal) - Number(r.payoutTotal)) >= 0 ? 'profit' : 'loss'}`}>
                            {(Number(r.betTotal) - Number(r.payoutTotal)).toFixed(1)}
                          </td>
                          <td className="time">{new Date(r.endTime || r.startTime).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* 高级分页控制 */}
              <div className="premium-pagination">
                <div className="pagination-info">
                  <span>第 {logsPage} 页，共 {logsTotalPages} 页</span>
                </div>
                <div className="pagination-controls">
                  <Button 
                    className="page-btn prev" 
                    disabled={logsPage <= 1} 
                    onClick={goPrevLogs}
                  >
                    <span>⟨</span> 上一页
                  </Button>
                  <Button 
                    className="page-btn next" 
                    disabled={logsPage >= logsTotalPages} 
                    onClick={goNextLogs}
                  >
                    下一页 <span>⟩</span>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h4>暂无回合记录</h4>
              <p>该设备还没有任何游戏记录</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="premium-modal-footer">
          <Button 
            variant="outline-secondary" 
            className="close-btn"
            onClick={() => setShowLogsModal(false)}
          >
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* “账目” Modal */}
      <Modal 
        show={showLedgerModal} 
        onHide={() => setShowLedgerModal(false)} 
        size="xl"
        dialogClassName="premium-device-ledger-modal"
        backdrop="static"
        centered
      >
        <Modal.Header className="premium-modal-header" closeButton>
          <Modal.Title className="premium-modal-title">
            <div className="title-content">
              <div className="title-icon">💼</div>
              <div className="title-text">
                <h4>设备轮次账目</h4>
                <span className="device-name">{ledgerDevice?.name}</span>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="premium-modal-body">
          {deviceRounds.length > 0 ? (
            <>
              {/* 统计概览卡片 */}
              <div className="device-stats-overview">
                <div className="device-stat-card total-rounds">
                  <div className="stat-icon">🎯</div>
                  <div className="stat-content">
                    <div className="stat-number">{deviceRounds.length}</div>
                    <div className="stat-label">总轮次</div>
                  </div>
                </div>
                <div className="device-stat-card total-bet">
                  <div className="stat-icon">💸</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {deviceRounds.reduce((sum, r) => sum + Number(r.betTotal || 0), 0).toFixed(1)}
                    </div>
                    <div className="stat-label">总投注</div>
                  </div>
                </div>
                <div className="device-stat-card total-payout">
                  <div className="stat-icon">💰</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {deviceRounds.reduce((sum, r) => sum + Number(r.payoutTotal || 0), 0).toFixed(1)}
                    </div>
                    <div className="stat-label">总返还</div>
                  </div>
                </div>
                <div className="device-stat-card net-profit">
                  <div className="stat-icon">📊</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {deviceRounds.reduce((sum, r) => sum + (Number(r.betTotal || 0) - Number(r.payoutTotal || 0)), 0).toFixed(1)}
                    </div>
                    <div className="stat-label">净盈亏</div>
                  </div>
                </div>
              </div>

              {/* 时间筛选器 */}
              <div className="filter-section">
                <div className="filter-header">
                  <span className="filter-icon">📅</span>
                  <span className="filter-title">时间筛选</span>
                </div>
                <div className="filter-controls">
                  <FormControl
                    type="datetime-local"
                    value={filterDateTime}
                    onChange={e => setFilterDateTime(e.target.value)}
                    className="datetime-input"
                  />
                  <Button className="filter-btn" onClick={applyFilter}>
                    筛选
                  </Button>
                </div>
              </div>

              {/* 移动端卡片视图 */}
              <div className="mobile-card-view d-md-none">
                {deviceRounds.map(r => (
                  <div key={r._id} className="record-card">
                    <div className="card-header">
                      <span className="record-id">#{r._id.slice(-5)}</span>
                      <span className="record-status">
                        {r.endTime ? '已完成' : '进行中'}
                      </span>
                    </div>
                    <div className="card-body">
                      <div className="record-row">
                        <span className="label">开始:</span>
                        <span className="value">{new Date(r.startTime).toLocaleString()}</span>
                      </div>
                      <div className="record-row">
                        <span className="label">结束:</span>
                        <span className="value">{r.endTime ? new Date(r.endTime).toLocaleString() : '—'}</span>
                      </div>
                      <div className="record-row">
                        <span className="label">投注:</span>
                        <span className="value">{Number(r.betTotal).toFixed(1)}</span>
                      </div>
                      <div className="record-row">
                        <span className="label">返还:</span>
                        <span className="value">{Number(r.payoutTotal).toFixed(1)}</span>
                      </div>
                      <div className="record-row">
                        <span className="label">盈亏:</span>
                        <span className={`value ${(Number(r.betTotal) - Number(r.payoutTotal)) >= 0 ? 'profit' : 'loss'}`}>
                          {(Number(r.betTotal) - Number(r.payoutTotal)).toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 桌面端表格视图 */}
              <div className="desktop-table-view d-none d-md-block">
                <div className="premium-table-container">
                  <Table className="premium-table">
                    <thead>
                      <tr>
                        <th>轮次ID</th>
                        <th>开始时间</th>
                        <th>结束时间</th>
                        <th>投注总额</th>
                        <th>返还总额</th>
                        <th>盈亏</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceRounds.map(r => (
                        <tr key={r._id}>
                          <td className="record-id">#{r._id.slice(-5)}</td>
                          <td className="time">{new Date(r.startTime).toLocaleString()}</td>
                          <td className="time">{r.endTime ? new Date(r.endTime).toLocaleString() : '—'}</td>
                          <td className="amount">{Number(r.betTotal).toFixed(1)}</td>
                          <td className="amount">{Number(r.payoutTotal).toFixed(1)}</td>
                          <td className={`amount ${(Number(r.betTotal) - Number(r.payoutTotal)) >= 0 ? 'profit' : 'loss'}`}>
                            {(Number(r.betTotal) - Number(r.payoutTotal)).toFixed(1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* 高级分页控制 */}
              <div className="premium-pagination">
                <div className="pagination-info">
                  <span>第 {roundsPage} 页，共 {roundsTotalPages} 页</span>
                </div>
                <div className="pagination-controls">
                  <Button 
                    className="page-btn prev" 
                    disabled={roundsPage <= 1} 
                    onClick={goPrevRounds}
                  >
                    <span>⟨</span> 上一页
                  </Button>
                  <Button 
                    className="page-btn next" 
                    disabled={roundsPage >= roundsTotalPages} 
                    onClick={goNextRounds}
                  >
                    下一页 <span>⟩</span>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">💼</div>
              <h4>暂无账目记录</h4>
              <p>该设备还没有任何轮次记录</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="premium-modal-footer">
          <Button 
            variant="outline-danger" 
            className="delete-btn"
            onClick={handleDeleteAllRounds}
          >
            🗑️ 删除账目
          </Button>
          <Button 
            variant="outline-secondary" 
            className="close-btn"
            onClick={() => setShowLedgerModal(false)}
          >
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* “下分记录” Modal */}
      <Modal 
        show={showDebitsModal} 
        onHide={() => setShowDebitsModal(false)} 
        size="xl"
        dialogClassName="premium-device-debits-modal"
        backdrop="static"
        centered
      >
        <Modal.Header className="premium-modal-header" closeButton>
          <Modal.Title className="premium-modal-title">
            <div className="title-content">
              <div className="title-icon">💰</div>
              <div className="title-text">
                <h4>下分记录</h4>
                <span className="device-name">{debitsDevice?.name}</span>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="premium-modal-body">
          {debits.length > 0 ? (
            <>
              {/* 统计概览卡片 */}
              <div className="device-stats-overview">
                <div className="device-stat-card total-records">
                  <div className="stat-icon">📈</div>
                  <div className="stat-content">
                    <div className="stat-number">{debits.length}</div>
                    <div className="stat-label">总记录数</div>
                  </div>
                </div>
                <div className="device-stat-card total-original">
                  <div className="stat-icon">🎯</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {debits.reduce((sum, d) => sum + Number(d.originalFs || 0), 0).toFixed(1)}
                    </div>
                    <div className="stat-label">原始FS总额</div>
                  </div>
                </div>
                <div className="device-stat-card total-debit">
                  <div className="stat-icon">💎</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {debits.reduce((sum, d) => sum + Number(d.debit || 0), 0).toFixed(1)}
                    </div>
                    <div className="stat-label">实际下分总额</div>
                  </div>
                </div>
                <div className="device-stat-card average-conversion">
                  <div className="stat-icon">⚡</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {debits.length > 0 ? (
                        (debits.reduce((sum, d) => sum + Number(d.debit || 0), 0) / 
                         debits.reduce((sum, d) => sum + Number(d.originalFs || 0), 0) * 100).toFixed(1)
                      ) : '0.0'}%
                    </div>
                    <div className="stat-label">平均转换率</div>
                  </div>
                </div>
              </div>

              {/* 移动端卡片视图 */}
              <div className="mobile-card-view d-md-none">
                {debits.map(rec => (
                  <div key={rec._id} className="debit-card">
                    <div className="card-header">
                      <span className="debit-player">{rec.user?.username || '未知'}</span>
                      <span className="debit-time">
                        {new Date(rec.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="card-body">
                      <div className="debit-row">
                        <span className="label">原始FS:</span>
                        <span className="value original">{Number(rec.originalFs).toFixed(1)}</span>
                      </div>
                      <div className="debit-row">
                        <span className="label">实际下分:</span>
                        <span className="value debit">{Number(rec.debit).toFixed(1)}</span>
                      </div>
                      <div className="debit-row">
                        <span className="label">转换率:</span>
                        <span className="value conversion">
                          {((Number(rec.debit) / Number(rec.originalFs)) * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="debit-full-time">
                        {new Date(rec.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 桌面端表格视图 */}
              <div className="desktop-table-view d-none d-md-block">
                <div className="premium-table-container">
                  <Table className="premium-table">
                    <thead>
                      <tr>
                        <th>时间</th>
                        <th>玩家</th>
                        <th>FS 原始值</th>
                        <th>实际下分</th>
                        <th>转换率</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debits.map(rec => (
                        <tr key={rec._id}>
                          <td className="time">{new Date(rec.timestamp).toLocaleString()}</td>
                          <td className="player-name">{rec.user?.username || '未知'}</td>
                          <td className="amount original">{Number(rec.originalFs).toFixed(1)}</td>
                          <td className="amount debit">{Number(rec.debit).toFixed(1)}</td>
                          <td className="conversion">
                            {((Number(rec.debit) / Number(rec.originalFs)) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* 高级分页控制 */}
              <div className="premium-pagination">
                <div className="pagination-info">
                  <span>第 {debitsPage} 页，共 {debitsTotalPages} 页</span>
                </div>
                <div className="pagination-controls">
                  <Button 
                    className="page-btn prev" 
                    disabled={debitsPage <= 1} 
                    onClick={goPrevDebits}
                  >
                    <span>⟨</span> 上一页
                  </Button>
                  <Button 
                    className="page-btn next" 
                    disabled={debitsPage >= debitsTotalPages} 
                    onClick={goNextDebits}
                  >
                    下一页 <span>⟩</span>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">💰</div>
              <h4>暂无下分记录</h4>
              <p>该设备还没有任何下分操作记录</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="premium-modal-footer">
          <Button 
            variant="outline-secondary" 
            className="close-btn"
            onClick={() => setShowDebitsModal(false)}
          >
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 添加分区 Modal */}
      <Modal show={showAddPartitionModal} onHide={closeAddPartitionModal}>
        <Modal.Header closeButton><Modal.Title>添加分区</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>分区名称</Form.Label>
              <Form.Control
                value={newPartitionName}
                onChange={e => setNewPartitionName(e.target.value)}
                placeholder="请输入分区名称"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>分区描述</Form.Label>
              <Form.Control
                value={newPartitionDesc}
                onChange={e => setNewPartitionDesc(e.target.value)}
                placeholder="可选：分区描述"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAddPartitionModal}>取消</Button>
          <Button variant="primary" onClick={handleSavePartition}>保存</Button>
        </Modal.Footer>
      </Modal>

      {/* 编辑分区 Modal */}
      <Modal show={showEditPartitionModal} onHide={closeEditPartitionModal}>
        <Modal.Header closeButton><Modal.Title>编辑分区</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>分区名称</Form.Label>
              <Form.Control
                value={editPartitionName}
                onChange={e => setEditPartitionName(e.target.value)}
                placeholder="请输入分区名称"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>分区描述</Form.Label>
              <Form.Control
                value={editPartitionDesc}
                onChange={e => setEditPartitionDesc(e.target.value)}
                placeholder="可选：分区描述"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditPartitionModal}>取消</Button>
          <Button variant="primary" onClick={handleUpdatePartition}>保存</Button>
        </Modal.Footer>
      </Modal>

      {/* 删除分区 Modal */}
      <Modal show={showDeletePartitionModal} onHide={cancelDeletePartition}>
        <Modal.Header closeButton><Modal.Title>确认删除分区</Modal.Title></Modal.Header>
        <Modal.Body>确认删除分区“{partitionToDelete?.name}”？此操作不可撤销。</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDeletePartition}>取消</Button>
          <Button variant="danger" onClick={confirmDeletePartition}>删除</Button>
        </Modal.Footer>
      </Modal>

      {/* 编辑履历 Modal */}
      <Modal show={showHistogramModal} onHide={() => setShowHistogramModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>编辑 Jackpot 履历</Modal.Title></Modal.Header>
        <Modal.Body>
          <p><strong>设备：</strong>{histogramDevice?.name} ({histogramDevice?._id})</p>
          <div style={{ overflowX: 'auto' }}>
            <Table bordered hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>连续奖项数</th>
                  <th>转数</th>
                  <th>时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {histogramData.map((row, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>
                      <Form.Control
                        type="number"
                        min={0}
                        value={row.consecutivePrize}
                        onChange={e => updateHistogramField(idx, 'consecutivePrize', e.target.value)}
                        style={{ width: 90 }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min={0}
                        value={row.turnCount}
                        onChange={e => updateHistogramField(idx, 'turnCount', e.target.value)}
                        style={{ width: 90 }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="datetime-local"
                        value={row.timestamp ? new Date(row.timestamp).toISOString().slice(0, 16) : ''}
                        onChange={e => updateHistogramField(idx, 'timestamp', new Date(e.target.value))}
                        style={{ width: 180 }}
                      />
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteHistogramRow(idx)}
                        disabled={histogramData.length <= 1}
                      >
                        删除
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>            <Button
              variant="outline-success"
              size="sm"
              onClick={handleAddHistogramRow}
              disabled={histogramData.length >= 20}
            >
              ➕ 添加一条
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistogramModal(false)}>取消</Button>
          <Button variant="primary" onClick={handleSaveHistogram} disabled={editingHistogram}>
            {editingHistogram ? '保存中...' : '保存'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ====== 设备管理模态框样式 ====== */}
      <style jsx>{`
        /* ====== 设备记录模态框样式 ====== */
        .premium-device-logs-modal .modal-dialog {
          max-width: 1200px !important;
          margin: 20px auto !important;
        }

        .premium-device-logs-modal .modal-content {
          border: none;
          border-radius: 20px;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.6),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
          overflow: hidden;
          position: relative;
        }

        .premium-device-logs-modal .modal-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 70%, rgba(16, 185, 129, 0.05) 0%, transparent 50%);
          pointer-events: none;
          z-index: 1;
        }

        /* ====== 设备账目模态框样式 ====== */
        .premium-device-ledger-modal .modal-dialog {
          max-width: 1200px !important;
          margin: 20px auto !important;
        }

        .premium-device-ledger-modal .modal-content {
          border: none;
          border-radius: 20px;
          background: linear-gradient(135deg, #0f1419 0%, #1a202c 50%, #2d3748 100%);
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.6),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
          overflow: hidden;
          position: relative;
        }

        .premium-device-ledger-modal .modal-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 70%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
          pointer-events: none;
          z-index: 1;
        }

        /* ====== 设备下分记录模态框样式 ====== */
        .premium-device-debits-modal .modal-dialog {
          max-width: 1200px !important;
          margin: 20px auto !important;
        }

        .premium-device-debits-modal .modal-content {
          border: none;
          border-radius: 20px;
          background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%);
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.6),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
          overflow: hidden;
          position: relative;
        }

        .premium-device-debits-modal .modal-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 20%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(79, 70, 229, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 70%, rgba(147, 51, 234, 0.05) 0%, transparent 50%);
          pointer-events: none;
          z-index: 1;
        }

        /* ====== 通用模态框头部样式 ====== */
        .premium-modal-header {
          background: rgba(255, 255, 255, 0.05);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding: 24px 32px;
          position: relative;
          z-index: 2;
        }

        .premium-modal-title {
          margin: 0;
          color: white;
        }

        .title-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .title-icon {
          font-size: 2rem;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
        }

        .title-text h4 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .device-name {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.7);
          font-weight: 500;
        }

        /* ====== 模态框主体样式 ====== */
        .premium-modal-body {
          padding: 32px;
          position: relative;
          z-index: 2;
          color: white;
          max-height: 70vh;
          overflow-y: auto;
        }

        /* ====== 统计概览卡片样式 ====== */
        .device-stats-overview {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 32px;
        }

        .device-stat-card {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          transition: all 0.3s ease;
          position: relative;
                   overflow: hidden;
        }

        .device-stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          transition: left 0.6s;
        }

        .device-stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 25px rgba(0, 0, 0, 0.3);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .device-stat-card:hover::before {
          left: 100%;
        }

        .device-stat-card .stat-icon {
          font-size: 2rem;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
        }

        .device-stat-card .stat-content {
          flex: 1;
        }

        .device-stat-card .stat-number {
          font-size: 1.8rem;
          font-weight: 700;
          color: white;
          margin-bottom: 4px;
        }

        .device-stat-card .stat-label {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.7);
          font-weight: 500;
        }

        /* ====== 时间筛选器样式 ====== */
        .filter-section {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
        }

        .filter-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }

        .filter-icon {
          font-size: 1.2rem;
        }

        .filter-title {
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
        }

        .filter-controls {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .datetime-input {
          background: rgba(255, 255, 255, 0.1) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          border-radius: 8px !important;
          color: white !important;
          padding: 8px 12px !important;
        }

        .datetime-input:focus {
          background: rgba(255, 255, 255, 0.15) !important;
          border-color: rgba(59, 130, 246, 0.5) !important;
          box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25) !important;
        }

        .filter-btn {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
          border: none !important;
          color: white !important;
          padding: 8px 20px !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          transition: all 0.3s ease !important;
        }

        .filter-btn:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2) !important;
        }

        /* ====== 移动端卡片视图样式 ====== */
        .mobile-card-view {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .record-card, .debit-card {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .record-card:hover, .debit-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .card-header {
          background: rgba(255, 255, 255, 0.1);
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .record-id {
          font-weight: 600;
          color: #3b82f6;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .record-player, .debit-player {
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }

        .record-status {
          font-size: 0.8rem;
          padding: 4px 8px;
          border-radius: 12px;
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          font-weight: 500;
        }

        .debit-time {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.6);
        }

        .card-body {
          padding: 16px;
        }

        .record-row, .debit-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .record-row:last-child, .debit-row:last-child {
          margin-bottom: 0;
        }

        .record-row .label, .debit-row .label {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.7);
          font-weight: 500;
        }

        .record-row .value, .debit-row .value {
          font-weight: 600;
          color: white;
        }

        .value.profit {
          color: #10b981 !important;
        }

        .value.loss {
          color: #ef4444 !important;
        }

        .value.original {
          color: #f59e0b !important;
        }

        .value.debit {
          color: #8b5cf6 !important;
        }

        .value.conversion {
          color: #06b6d4 !important;
        }

        .record-time, .debit-full-time {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.6);
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        /* ====== 桌面端表格样式 ====== */
        .premium-table-container {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          overflow: hidden;
          backdrop-filter: blur(10px);
        }

        .premium-table {
          margin: 0 !important;
          color: white !important;
          background: transparent !important;
        }

        .premium-table thead th {
          background: rgba(255, 255, 255, 0.1) !important;
          border: none !important;
          padding: 16px 12px !important;
          font-weight: 600 !important;
          color: rgba(255, 255, 255, 0.9) !important;
          font-size: 0.9rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
        }

        .premium-table tbody td {
          background: transparent !important;
          border: none !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
          padding: 14px 12px !important;
          color: rgba(255, 255, 255, 0.9) !important;
          vertical-align: middle !important;
        }

        .premium-table tbody tr:hover {
          background: rgba(255, 255, 255, 0.05) !important;
        }

        .premium-table .record-id {
          font-family: 'Monaco', 'Menlo', monospace;
          font-weight: 600;
          color: #3b82f6 !important;
        }

        .premium-table .player-name {
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9) !important;
        }

        .premium-table .amount {
          font-weight: 600;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .premium-table .amount.profit {
          color: #10b981 !important;
        }

        .premium-table .amount.loss {
          color: #ef4444 !important;
        }

        .premium-table .amount.original {
          color: #f59e0b !important;
        }

        .premium-table .amount.debit {
          color: #8b5cf6 !important;
        }

        .premium-table .time {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.7) !important;
        }

        .premium-table .conversion {
          font-weight: 600;
          color: #06b6d4 !important;
        }

        /* ====== 高级分页控制样式 ====== */
        .premium-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 24px;
          padding: 20px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .pagination-info {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.9rem;
          font-weight: 500;
        }

        .pagination-controls {
          display: flex;
          gap: 12px;
        }

        .page-btn {
          background: rgba(255, 255, 255, 0.1) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          color: white !important;
          padding: 8px 16px !important;
          border-radius: 8px !important;
          font-weight: 500 !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
        }

        .page-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.2) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
        }

        .page-btn:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }

        .page-btn span {
          font-size: 0.8rem;
        }

        /* ====== 空状态样式 ====== */
        .empty-state {
          text-align: center;
          padding: 60px 40px;
          color: rgba(255, 255, 255, 0.7);
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 20px;
          opacity: 0.6;
        }

        .empty-state h4 {
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 12px;
          font-weight: 600;
        }

        .empty-state p {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.9rem;
        }

        /* ====== 模态框底部样式 ====== */
        .premium-modal-footer {
          background: rgba(255, 255, 255, 0.05);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding: 20px 32px;
          position: relative;
          z-index: 2;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.1) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          color: white !important;
          padding: 10px 24px !important;
          border-radius: 8px !important;
          font-weight: 500 !important;
          transition: all 0.3s ease !important;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.2) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          transform: translateY(-2px) !important;
        }

        .delete-btn {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
          border: none !important;
          color: white !important;
          padding: 10px 24px !important;
          border-radius: 8px !important;
          font-weight: 500 !important;
          transition: all 0.3s ease !important;
        }

        .delete-btn:hover {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3) !important;
        }

        /* ====== 响应式设计 ====== */
        @media (max-width: 768px) {
          .premium-device-logs-modal .modal-dialog,
          .premium-device-ledger-modal .modal-dialog,
          .premium-device-debits-modal .modal-dialog {
            margin: 10px !important;
            max-width: calc(100vw - 20px) !important;
          }

          .premium-modal-header,
          .premium-modal-body,
          .premium-modal-footer {
            padding: 20px;
          }

          .device-stats-overview {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 24px;
          }

          .device-stat-card {
            padding: 16px;
            flex-direction: column;
            text-align: center;
            gap: 8px;
          }

          .device-stat-card .stat-number {
            font-size: 1.4rem !important;
          }

          .title-content {
            gap: 12px;
          }

          .title-icon {
            font-size: 1.5rem;
          }

          .title-text h4 {
            font-size: 1.2rem;
          }

          .premium-pagination {
            flex-direction: column;
            gap: 12px;
            text-align: center;
          }

          .filter-controls {
            flex-direction: column;
            gap: 8px;
          }

          .datetime-input {
            width: 100% !important;
          }
        }

        @media (max-width: 480px) {
          .device-stats-overview {
            grid-template-columns: 1fr;
          }

          .premium-modal-header,
          .premium-modal-body,
          .premium-modal-footer {
            padding: 16px;
          }

          .device-stat-card .stat-number {
            font-size: 1.2rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DeviceManagement;